import Tea from '@dp/byted-tea-sdk-oversea';
import { SWRConfig } from 'swr';

export const CLIENT_EVENT_PARAM_KEY = 'CLIENT_EVENT_PARAM_KEY';

/**
 * define new events in this map
 */
const events = new Map([
  ['home_page_view', ['tt4b_core_user_id']],
  ['home_page_app_click', ['tt4b_core_user_id']],
  ['app_details_page_full_view', ['tt4b_core_user_id', 'app_type', 'app_name', 'app_id']],
  ['app_details_page_full_open_click', ['tt4b_core_user_id', 'app_type', 'app_name', 'app_id']],
  ['app_details_page_full_install_click', ['tt4b_core_user_id', 'app_type', 'app_name', 'app_id']],
  [
    'app_details_page_full_uninstall_click',
    ['tt4b_core_user_id', 'app_type', 'app_name', 'app_id'],
  ],
  ['home_page_login', []],
  ['iframe_left_nav_click', ['tt4b_core_user_id', 'app_type', 'app_name', 'app_id']],
  ['iframe_left_nav_imps', ['tt4b_core_user_id', 'app_type', 'app_name', 'app_id']],
  ['iframe_imps', ['tt4b_core_user_id', 'app_type', 'app_name', 'app_id']],
] as const);

type EventName = typeof events extends Map<infer K, any> ? K : never;

export type EventParams = (typeof events extends Map<any, infer V> ? V : never)[number];

let teaReady = false;
Tea.once('ready', () => {
  teaReady = true;
});
/**
 * define param getter in this object
 * the getter returns a string when the value is available
 * otherwise returns null
 */
const paramValGetterMap: Record<EventParams, () => string | null> = {
  tt4b_core_user_id: () => {
    const res = SWRConfig.defaultValue.cache.get(CLIENT_EVENT_PARAM_KEY); // https://github.com/vercel/swr/issues/2352#issuecomment-1464738845
    return res?.data?.tt4b_core_user_id || '';
  },
  app_id: () => {
    const segments = location.pathname.split('/').filter(Boolean);
    const appIdFromPath = [...segments].reverse().find((segment) => /^\d+$/.test(segment));
    if (!appIdFromPath) {
      return null;
    }
    return appIdFromPath;
  },
  app_name: () => {
    const res = SWRConfig.defaultValue.cache.get(CLIENT_EVENT_PARAM_KEY);
    return res?.data?.app_name || '';
  },
  app_type: () => {
    const res = SWRConfig.defaultValue.cache.get(CLIENT_EVENT_PARAM_KEY);
    return res?.data?.app_type || '';
  },
};

export type TriggerParams = {
  eventName: EventName;
  extraParams?: null | Record<string, string>;
  useBeacon?: boolean;
};
export function sendPredefinedEvent(args: TriggerParams) {
  if (!teaReady) {
    // wait until Tea is ready to send events
    setTimeout(() => sendPredefinedEvent(args), 100);
    return;
  }
  const { eventName, extraParams, useBeacon } = args;
  const params = events.get(eventName);
  if (!params) return;
  const valGetters = params.map((param) => paramValGetterMap[param]);

  const report = (values: string[]) => {
    if (values.length !== params.length) {
      // should be impossible
      return;
    }
    const finalParams = {
      ...params.reduce(
        (prev, curr, index) =>
          Object.assign(prev, {
            [curr]: values[index],
          }),
        {}
      ),
      ...extraParams,
    };
    if (useBeacon) {
      Tea.beaconEvent(eventName, finalParams);
    } else {
      Tea.event(eventName, finalParams);
    }
  };

  try {
    const values = valGetters.map((getter) => {
      const ret = getter();
      if (ret === null) {
        throw new Error('not available');
      }
      return ret;
    });
    report(values);
  } catch {
    // some values are not available try again in a few seconds
    setTimeout(() => {
      report(valGetters.map((getter) => getter()).map((val) => val || ''));
    }, 3000);
  }
}
